import React, { useRef, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";

import useAuth from "../../hooks/useAuth";

const AddCandidate = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const jobCod = location.state?.jobCode;
  const [selectedJobCode, setSelectedJobCode] = useState(jobCod || "");
  const [jobCode, setJobCode] = useState([]);
  const [jobSkills, setJobSkills] = useState(null);
  const [selectedTechSkills, setSelectedTechSkills] = useState([]);
  const [openingsCount, setOpeningsCount] = useState(4);
  const [candidates, setCandidates] = useState([]); // Start empty
  const [expanded, setExpanded] = useState({});
  const [files, setFiles] = useState({});
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [alertType, setAlertType] = useState("");
  const fileInputRefs = useRef({});

  const { token, supplierId } = useAuth();

  const config = React.useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const handleAlert = () => {
    setAlert(false);
  };

  // ... (handleFileChange and other valid functions stay same, skipping for brevity in replacement if possible, but replace_file_content needs contiguous block)
  // Actually, I should just replace the specific function blocks or the hooks area.

  // Let's verify line numbers. config is around line 61.
  // getJobCode starts around line 160.
  // getSkills starts around line 173.

  // I will make separate replacements.




  // const handleFileChange = (event, docType) => {
  //   const selectedFile = event.target.files[0];
  //   if (selectedFile && selectedFile.type === "application/pdf") {
  //     setFiles((prevFiles) => ({
  //       ...prevFiles,
  //       [docType]: selectedFile,
  //     }));
  //   } else {
  //     setAlertContent("Invalid file type. Please select a PDF file.");
  //     setAlertType("error");
  //     setAlert(true);
  //   }
  // };

  const handleFileChange = (event, docType) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedMimeTypes = [
        'application/msword', // .doc        
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/pdf' // .pdf
      ];
      const allowedExtensions = ['.doc', '.docx', '.pdf'];

      // Check MIME type (primary validation)
      const isValidMime = allowedMimeTypes.includes(selectedFile.type);

      // Fallback: Check file extension
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase();
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (isValidMime || isValidExtension) {
        // Optional: Additional size validation (e.g., max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
          setAlertContent("File too large. Please select a document under 5MB.");
          setAlertType("error");
          setAlert(true);
          event.target.value = ''; // Clear input
          return;
        }

        setFiles((prevFiles) => ({
          ...prevFiles,
          [docType]: selectedFile,
        }));
      } else {
        setAlertContent("Invalid file type. Please select a Word document (.doc or .docx) or a PDF (.pdf).");
        setAlertType("error");
        setAlert(true);
        event.target.value = ''; // Clear invalid file from input
      }
    }
  };


  const handleRemoveFile = (docType) => {
    setFiles((prevFiles) => {
      const newFiles = { ...prevFiles };
      delete newFiles[docType];
      return newFiles;
    });
  };

  const addCandidate = () => {
    const newIndex = candidates.length;
    setCandidates([...candidates, {}]);
    setExpanded((prev) => ({ ...prev, [`panel${newIndex}`]: true }));
  };

  const removeCandidate = (index) => {
    const newCandidates = candidates.filter((_, i) => i !== index);
    setCandidates(newCandidates);

    // Remove associated file
    const newFiles = { ...files };
    delete newFiles[`Resume_${index}`];
    setFiles(newFiles);

    // Update expanded state
    const newExpanded = { ...expanded };
    delete newExpanded[`panel${index}`];
    setExpanded(newExpanded);
  };

  const toggleAccordion = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [`panel${index}`]: !prev[`panel${index}`],
    }));
  };

  const getJobCode = React.useCallback(async () => {
    let url = `/api/job/jobcode?supplierId=${supplierId}`;
    try {
      const res = await API.get(url, config);
      setJobCode(res.data.result);
    } catch (error) {
      console.log(error);
      setAlertContent(error?.response?.data?.message);
      setAlertType("error");
      setAlert(true);
    }
  }, [supplierId, config]);

  const getSkills = React.useCallback(async () => {
    if (!selectedJobCode) return;

    let url = `/api/job/skills/${selectedJobCode}`;
    try {
      const res = await API.get(url, config);
      const data = res.data;
      setJobSkills(data);
      setSelectedTechSkills(data.techSkills || []);
      setOpeningsCount(data.openings);

      // Start with exactly 1 candidate
      setCandidates([{}]);
      setExpanded({ "panel0": true }); // Expand first accordion by default
    } catch (error) {
      console.log(error);
      setAlertContent(error?.response?.data?.message);
      setAlertType("error");
      setAlert(true);
    }
  }, [selectedJobCode, config]);

  const normalizedLocations = React.useMemo(() => {
    const loc = jobSkills?.locations;

    if (!loc) return [];

    if (Array.isArray(loc)) return loc;

    if (typeof loc === "string") {
      try {
        const parsed = JSON.parse(loc);
        if (Array.isArray(parsed)) return parsed;
        return [parsed];            // parsed is a single string
      } catch {
        // plain string like "Pune"
        return [loc];
      }
    }

    return [];
  }, [jobSkills?.locations]);

  const handleBulkSubmit = async () => {
    // Collect valid candidates
    const validCandidates = [];
    const fileKeys = [];

    candidates.forEach((_, index) => {
      const fileKey = `Resume_${index}`;
      if (!files[fileKey]) {
        setIsFormSubmitted(true);
        return;
      }

      // Get form values from nested formik refs (simplified validation)
      const candidateData = {
        candidate_name: document.querySelector(`[name="candidate_name_${index}"]`)?.value || "",
        candidate_current_location: document.querySelector(`[name="candidate_current_location_${index}"]`)?.value || "",
        mobile: document.querySelector(`[name="mobile_${index}"]`)?.value || "",
        email: document.querySelector(`[name="email_${index}"]`)?.value || "",
        currency: document.querySelector(`[name="currency_${index}"]`)?.value || "",
        budget: document.querySelector(`[name="budget_${index}"]`)?.value || "",
        budget_eom: document.querySelector(`[name="budget_eom_${index}"]`)?.value || "",
        notice_period: document.querySelector(`[name="notice_period_${index}"]`)?.value || "",
        important_note: document.querySelector(`[name="important_note_${index}"]`)?.value || "",
        coreSkillId: jobSkills?.coreSkills?.id || "",
        techSkills: selectedTechSkills,
      };

      if (candidateData.candidate_name && candidateData.email && candidateData.mobile) {
        validCandidates.push(candidateData);
        fileKeys.push(fileKey);
      }
    });

    if (validCandidates.length === 0) {
      setAlertContent("Please fill at least one valid candidate with resume");
      setAlertType("error");
      setAlert(true);
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append("job_code", selectedJobCode);
    formData.append("candidates", JSON.stringify(validCandidates));
    formData.append("suppliercode", supplierId);

    // Append files
    fileKeys.forEach((key) => {
      if (files[key]) {
        formData.append("resumes", files[key]);
      }
    });

    try {
      const res = await API.post("/api/profile/addbulkprofile", formData, config);
      setAlertContent(res.data.message);
      setAlertType("success");
      setAlert(true);

      // Invalidate React Query cache
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });

      setTimeout(() => {
        const from = location.state?.from || "";
        navigate("/candidate_data" + from);
      }, 1500);
    } catch (error) {
      setAlertContent(error.response?.data?.message || "Upload failed");
      setAlertType("error");
      setAlert(true);
    }
  };



  useEffect(() => {
    getJobCode();
  }, [getJobCode]);

  useEffect(() => {
    if (selectedJobCode) {
      getSkills();
    }
  }, [selectedJobCode, getSkills]);

  useEffect(() => {
    const clearLocationState = (event) => {
      navigate({ ...location, state: {} });
    };
    window.addEventListener("beforeunload", clearLocationState);
    return () => {
      window.removeEventListener("beforeunload", clearLocationState);
    };
  }, [navigate, location]);

  const navigateToPreviousPage = () => {
    const from = location.state?.from || "";
    if (from) {
      navigate("/candidate_data" + from);
    } else {
      navigate(-1);
    }
  };

  const allCurrency = [
    { id: 1, value: "INR", label: "INR" },
    { id: 2, value: "USD", label: "USD" },
    { id: 3, value: "GBP", label: "GBP" },
  ];

  const budgetUOM = [
    { id: 1, value: "perHour", label: "Per Hour" },
    { id: 2, value: "perMonth", label: "Per Month" },
    { id: 3, value: "perDay", label: "Per Day" },
  ];

  const FileUploadSection = ({ index }) => (
    <div style={{ marginTop: 20, border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", backgroundColor: "#fafafa" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xl={3} lg={3} md={3} sm={4} xs={12}>
          <Typography variant="body2" fontWeight={500}>Upload Resume *</Typography>
        </Grid>
        <Grid item xl={6} lg={6} md={6} sm={6} xs={12}>
          {files[`Resume_${index}`] ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                {files[`Resume_${index}`].name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveFile(`Resume_${index}`)}
                title="Remove file"
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No file selected</Typography>
          )}
        </Grid>
        <Grid item xl={3} lg={3} md={3} sm={2} xs={12}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() => fileInputRefs.current[`Resume_${index}`]?.click()}
          >
            Browse
          </Button>
          <input
            type="file"
            hidden
            accept=".doc,.docx,.pdf"
            onChange={(e) => handleFileChange(e, `Resume_${index}`)}
            ref={(el) => {
              if (el) fileInputRefs.current[`Resume_${index}`] = el;
            }}
          />
        </Grid>
      </Grid>
    </div>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom mb={3}>
          Add Profiles
        </Typography>

        {/* Job Code Selection */}
        <Grid container spacing={3} mb={4}>
          <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
            {jobCod !== undefined ? (
              <TextField
                label="Job Code"
                value={selectedJobCode}
                fullWidth
                size="small"
                disabled
              />
            ) : (
              <FormControl fullWidth size="small">
                <Select
                  value={selectedJobCode}
                  onChange={(e) => setSelectedJobCode(e.target.value)}
                  displayEmpty
                  label="Job Code"
                >
                  <MenuItem value="" disabled>
                    Select Job Code
                  </MenuItem>
                  {jobCode?.map((job) => (
                    <MenuItem key={job.job_code} value={job.job_code}>
                      {job.job_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>


        {/* Job Details Accordion - Shows only after job code selection */}
        {selectedJobCode && jobSkills && (
          <Accordion defaultExpanded sx={{ mb: 4 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="job-details-content"
              id="job-details-header"
            >
              <Typography variant="h6" fontWeight={600}>
                Job Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Paper elevation={1} sx={{ overflow: "hidden" }}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600, width: "30%" }}>Job Code</TableCell>
                        <TableCell>{jobSkills.jobCode || selectedJobCode}</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600, width: "30%" }}>Openings</TableCell>
                        <TableCell>
                          <Chip label={jobSkills.openings || openingsCount} color="primary" size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Title</TableCell>
                        <TableCell>{jobSkills.title || 'N/A'}</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Core Skill</TableCell>
                        <TableCell>{jobSkills?.coreSkills?.name || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Experience</TableCell>
                        <TableCell>{jobSkills.experience || 'N/A'} years</TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Mode</TableCell>
                        <TableCell>{jobSkills.mode_of_work || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Budget</TableCell>
                        <TableCell>
                          {jobSkills.budget && `${jobSkills.currency || 'INR'} ${jobSkills.budget}`}
                          {jobSkills.budget_uom && ` / ${jobSkills.budget_uom}`}
                        </TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Timeline</TableCell>
                        <TableCell>{jobSkills.notice_period || 'N/A'} days</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Locations</TableCell>
                        <TableCell>
                          {normalizedLocations.length > 0 ? normalizedLocations.join(", ") : "N/A"}
                        </TableCell>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Background Verification</TableCell>
                        <TableCell>{jobSkills.background_verification === 'Yes' ? "Required" : "Not Required"}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant="head" sx={{ fontWeight: 600 }}>Tech Skills</TableCell>
                        <TableCell colSpan={3}>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {selectedTechSkills.map((skill, idx) => (
                              <Chip key={idx} label={skill} size="small" color="secondary" />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Job Description */}
              {jobSkills.job_description && (
                <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Job Description
                  </Typography>
                  <div
                    dangerouslySetInnerHTML={{ __html: jobSkills.job_description }}
                    style={{ fontSize: '0.875rem', lineHeight: 1.5 }}
                  />
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        )}


        {/* Candidate Accordions - Only show after job selection */}
        {selectedJobCode && jobSkills && (
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom mb={2}>
              Candidate Details ({candidates.length})
            </Typography>

            <Grid container spacing={2}>
              {/* Dynamic Accordions for Candidates */}
              {candidates.map((candidate, index) => (
                <Grid item xl={12} key={index}>
                  <Accordion
                    expanded={expanded[`panel${index}`]}
                    onChange={() => toggleAccordion(index)}
                    sx={{ boxShadow: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ width: "90%", flexShrink: 0, fontWeight: 500 }}>
                        Candidate {index + 1}
                        {files[`Resume_${index}`] && (
                          <Chip
                            label={files[`Resume_${index}`].name}
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      {candidates.length > 1 && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCandidate(index);
                          }}
                          size="small"
                          sx={{ ml: 'auto' }}
                        >
                          <DeleteIcon color="error" />
                        </IconButton>
                      )}
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {/* Candidate Name */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Candidate Name *
                          </Typography>
                          <TextField
                            name={`candidate_name_${index}`}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>

                        {/* Current Location */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Current Location *
                          </Typography>
                          <TextField
                            name={`candidate_current_location_${index}`}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>

                        {/* Mobile */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Mobile *
                          </Typography>
                          <TextField
                            name={`mobile_${index}`}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>

                        {/* Email */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Email *
                          </Typography>
                          <TextField
                            name={`email_${index}`}
                            type="email"
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>

                        {/* Currency */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Currency *
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select name={`currency_${index}`} defaultValue="">
                              {allCurrency.map((currency) => (
                                <MenuItem key={currency.id} value={currency.value}>
                                  {currency.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Budget */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Budget *
                          </Typography>
                          <TextField
                            name={`budget_${index}`}
                            fullWidth
                            size="small"
                            required
                            onInput={(e) =>
                              (e.target.value = e.target.value.replace(/[^0-9.]/g, ""))
                            }
                          />
                        </Grid>

                        {/* Budget UOM */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Budget UOM *
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select name={`budget_eom_${index}`} defaultValue="">
                              {budgetUOM.map((uom) => (
                                <MenuItem key={uom.id} value={uom.value}>
                                  {uom.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Notice Period */}
                        <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Timeline (days)
                          </Typography>
                          <TextField
                            name={`notice_period_${index}`}
                            fullWidth
                            size="small"
                            type="number"
                          />
                        </Grid>

                        {/* Notes */}
                        <Grid item xl={12}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            Notes (Optional)
                          </Typography>
                          <TextField
                            name={`important_note_${index}`}
                            multiline
                            rows={3}
                            placeholder="Enter important notes about the candidate..."
                            fullWidth
                            size="small"
                          />
                        </Grid>

                        {/* File Upload */}
                        <Grid item xl={12}>
                          <FileUploadSection index={index} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}

              {/* Add Candidate Button - ALWAYS SHOW (no limit) */}
              <Grid item xl={12}>
                <Button
                  variant="outlined"
                  onClick={addCandidate}
                  startIcon={<AddIcon />}
                  fullWidth
                  size="large"
                >
                  Add Another Candidate
                </Button>
              </Grid>

              {/* Submit Button */}
              <Grid item xl={12}>
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    size="large"
                    disabled={candidates.length === 0}
                    sx={{
                      background: "#2777AA",
                      px: 6,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                    onClick={handleBulkSubmit}
                  >
                    Submit All ({candidates.length} Candidates)
                  </Button>
                </Box>
              </Grid>

              {isFormSubmitted && (
                <Grid item xl={12}>
                  <Typography color="error" variant="body2" sx={{ textAlign: "center", mt: 1 }}>
                    Please upload resume for all candidates
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>

      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlert}
      />
    </div>
  );
};

export default AddCandidate;
